import { useState, useEffect } from "react";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SUBSCRIBERS = new Map<string, Set<(arg: any) => void>>();

export function useLocalStorage<T>(key: string, initialValue: T) {
  const isBrowser = typeof window !== "undefined";
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (!isBrowser) {
      return initialValue;
    }
    const item = window.localStorage.getItem(key);
    if (item) {
      try {
        return JSON.parse(item) as T;
      } catch (error) {
        console.warn(
          `Failed to parse localStorage key "${key}", resetting to initial value.`,
          error,
        );
        window.localStorage.removeItem(key);
      }
    }
    return initialValue;
  });

  useEffect(() => {
    if (!isBrowser) {
      return;
    }
    const handleStorageEvent = (e: StorageEvent) => {
      if (e.key === key && e.newValue !== null) {
        try {
          const newValue = JSON.parse(e.newValue) as T;
          setStoredValue(newValue);
        } catch (error) {
          console.error("Error parsing storage event value:", error);
        }
      } else if (e.key === key && e.newValue === null) {
        // Key was removed, reset to initial value
        setStoredValue(initialValue);
      }
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleValueUpdate = (arg: any) => setStoredValue(arg);

    window.addEventListener("storage", handleStorageEvent);
    let listeners = SUBSCRIBERS.get(key);
    if (listeners === undefined) {
      listeners = new Set();
      SUBSCRIBERS.set(key, listeners);
    }
    listeners.add(handleValueUpdate);
    return () => {
      window.removeEventListener("storage", handleStorageEvent);
      listeners.delete(handleValueUpdate);
    };
  }, [key, initialValue, setStoredValue, isBrowser]);

  const setValue = (value: T | ((prev: T) => T)) => {
    try {
      setStoredValue((prev) => {
        const resolvedValue =
          typeof value === "function"
            ? (value as (prev: T) => T)(prev)
            : value;
        if (Object.is(prev, resolvedValue)) {
          return prev;
        }
        if (typeof window !== "undefined") {
          window.localStorage.setItem(key, JSON.stringify(resolvedValue));
          const listeners = SUBSCRIBERS.get(key);
          if (listeners) {
            listeners.forEach((listener) => {
              listener(resolvedValue);
            });
          }
        }
        return resolvedValue;
      });
    } catch (error) {
      console.error("Error setting localStorage:", error);
    }
  };

  return [storedValue, setValue] as const;
}
